<script setup>
import axios from 'axios';
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlTag from '../xl/XlTag.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import IconMail from '../icons/IconMail.vue';
import IconUserMultiple from '../icons/IconUserMultiple.vue';
import MessageList from '../messages/MessageList.vue';
import MessageComposer from '../messages/MessageComposer.vue';
import CallRoom from '../messages/CallRoom.vue';
import SafeRichText from '../communications/SafeRichText.vue';
import { myCallLeaveUrl, reactToMine, readMyThread, sendMine } from '../../api/messages';
import { sessionsList } from '../../api/portal';
import { updateTime } from '../../helpers/messageTime';
import { timeState } from '../../helpers/sessionTimes';
import { useEventStore } from '../../stores/event';
import { listenForMessages } from '../../composables/useMessagesLive';

// A fallback for when the live connection is down.
const POLL_MS = 60000;
const QUICK = ['When does my policy renew?', 'I need to report damage', 'Change my address', 'Can I lower my price?'];
const ROLE = 'Your insurance advisor';
const TAGS = { renewal: { label: 'Renewal', tone: 'warning' }, notice: { label: 'Notice', tone: '' } };
const tagOf = (update) => TAGS[update.tag] ?? TAGS.notice;
const BLANK_UNITS = ['&nbsp;', '&#160;', '<br>', '<br/>', '<br />'];
const endsAt = (low, end, tag) => end >= tag.length && low.startsWith(tag, end - tag.length);
const blankLen = (low, end) => (/\s/.test(low[end - 1]) ? 1 : BLANK_UNITS.find((u) => endsAt(low, end, u))?.length ?? 0);
// `open` is the index just past a </p> that may close a blank paragraph; the cut falls back to it if that paragraph isn't blank.
const step = (low, end, open) => {
  const n = blankLen(low, end);
  if (n) return [end - n, open];
  const tag = open < 0 ? '</p>' : '<p>';
  if (!endsAt(low, end, tag)) return null;

  return open < 0 ? [end - 4, end] : [end - 3, -1];
};
const trimTail = (html) => {
  const text = String(html ?? '');
  const low = text.toLowerCase();
  let state = [low.length, -1];
  for (let next = step(low, ...state); next; next = step(low, ...next)) state = next;

  return text.slice(0, state[1] < 0 ? state[0] : state[1]);
};

const eventStore = useEventStore();
const router = useRouter();

const thread = ref({ broker: null, messages: [], attachment_rules: undefined });
const pendingMessage = ref(null);
const sending = ref(false);
let reloadWanted = false;
const loading = ref(true);
const loadError = ref('');
const sendError = ref('');
const call = ref(null);
const scroller = ref(null);
const composer = ref(null);
const officeHours = ref(null);
const now = ref(new Date());

const updates = ref([]);
const unread = ref(0);
const updatesTotal = ref(0);
const updatesPage = ref(0);
const hasPolicy = ref(false);
const hasEarlier = computed(() => updates.value.length < updatesTotal.value);
// Reading an update clears the shell's badge without a page change.
const portalUnread = inject('portalUnread', null);
watch(unread, () => portalUnread?.refresh());
const openUpdate = ref(null);

const org = computed(() => eventStore.event?.value?.login_event_name || 'your agency');
const broker = computed(() => thread.value.broker);
// Without a broker the conversation is with the agency itself.
const brokerName = computed(() => broker.value?.name || (broker.value ? 'your advisor' : org.value));
const initials = computed(() => brokerName.value.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase());
const brokerFirstName = computed(() => (broker.value?.name ? brokerName.value.split(/\s+/)[0] : 'your advisor'));

const replies = computed(() => {
  const minutes = broker.value?.usual_reply_minutes;
  if (minutes === null || minutes === undefined) return 'usually replies within a working day';
  if (minutes <= 60) return 'usually replies within an hour';
  if (minutes <= 480) return `usually replies within ${Math.ceil(minutes / 60)} hours`;

  return 'usually replies within a few working days';
});

const deskOpen = computed(() => Boolean(String(officeHours.value?.host?.name ?? '').trim()) && timeState(officeHours.value, now.value).live);

const whoLine = computed(() => {
  if (!broker.value) return 'No broker assigned yet';
  if (deskOpen.value) return `${ROLE} · in Office Hours now`;

  return `${ROLE} · ${replies.value}`;
});

const opener = computed(() => (broker.value
  ? `Only you and ${org.value} can see this. ${brokerName.value} is your broker and normally the one who replies.`
  : `Your broker hasn't been assigned yet. Write anything you like here — the first person available at ${org.value} will pick it up, and ${org.value} can see it in the meantime.`));

const emptyLine = computed(() => {
  if (!broker.value) return "Write the first message. Whoever picks it up can see your policies, so you don't have to explain them.";
  const reply = ` ${replies.value.charAt(0).toUpperCase()}${replies.value.slice(1)}.`;

  return `Ask ${brokerFirstName.value} anything — a renewal, a claim, a change of address.${reply}`;
});

const byTimeThenId = (a, b) =>
  String(a.sent_at ?? '').localeCompare(String(b.sent_at ?? ''))
  || String(a.id).localeCompare(String(b.id), undefined, { numeric: true });

const mergeMessages = (existing, incoming) =>
  [...new Map([...existing, ...incoming].map((m) => [m.id, m])).values()].sort(byTimeThenId);

const displayMessages = computed(() => (
  pendingMessage.value ? [...thread.value.messages, pendingMessage.value] : thread.value.messages
));

const applyIfIdle = (data) => {
  if (sending.value) {
    reloadWanted = true;
    return;
  }
  thread.value = {
    ...data,
    messages: mergeMessages(thread.value.messages, data.messages || []),
  };
  // The read marks the broker's messages read; the shell's own count raced it.
  portalUnread?.refresh();
  loadError.value = '';
};

const noteLoadError = () => {
  if (!sending.value) loadError.value = 'Your messages could not be loaded.';
};

const fetchThread = async () => {
  try {
    applyIfIdle(await readMyThread());
  } catch (e) {
    noteLoadError();
  }
  loading.value = false;
  takeDraft();
};

const load = async () => {
  if (sending.value) {
    reloadWanted = true;
    return;
  }
  await fetchThread();
};

// Staff previewing are refused /my/* — the header then simply offers no call.
const loadOfficeHours = async () => {
  try {
    officeHours.value = (await sessionsList())?.office_hours ?? null;
  } catch (e) {
    officeHours.value = null;
  }
};

const joinOfficeHours = () => router.push({ path: '/hub/sessions', query: { session: officeHours.value.id } });

// Each page answers has_policy only for the renewals on it.
const loadUpdates = async (page = 1) => {
  const feed = await axios.get(`${window.store.event_backend_api}/communications/mine`, { params: { page } })
    .then(({ data }) => data.data, () => null);
  if (!feed) return;

  const listed = new Set(updates.value.map((u) => u.id));
  updates.value = [...updates.value, ...feed.data.filter((u) => !listed.has(u.id))];
  unread.value = feed.unread;
  updatesTotal.value = feed.total;
  updatesPage.value = page;
  hasPolicy.value = hasPolicy.value || Boolean(feed.has_policy);
};

const showEarlier = () => loadUpdates(updatesPage.value + 1);

const markAllRead = async () => {
  await axios.post(`${window.store.event_backend_api}/communications/mine/read`);
  updates.value = updates.value.map((u) => ({ ...u, read: true }));
  unread.value = 0;
};

const makePendingBubble = (body, files) => ({
  id: 'pending',
  body,
  sending: true,
  uploadProgress: null,
  attachments: files.map((f) => ({ id: f.name, name: f.name, size: f.size })),
  mine: true,
});

const onUploadProgress = (event) => {
  if (!pendingMessage.value) return;
  pendingMessage.value = {
    ...pendingMessage.value,
    uploadProgress: Math.round((event.loaded * 100) / event.total),
  };
};

const finishSend = () => {
  pendingMessage.value = null;
  sending.value = false;
  if (!reloadWanted) return;
  reloadWanted = false;
  load();
};

// A failed send rejects to the composer, which keeps the message and offers to retry it.
const send = async ({ body, files = [] }) => {
  pendingMessage.value = makePendingBubble(body, files);
  sending.value = true;
  try {
    const created = await sendMine(body, files, onUploadProgress);
    thread.value = { ...thread.value, messages: mergeMessages(thread.value.messages, [created]) };
  } finally {
    finishSend();
  }
};

const toggleUpdate = async (update) => {
  openUpdate.value = openUpdate.value === update.id ? null : update.id;
  if (openUpdate.value === null || update.read) return;

  update.read = true;
  try {
    const { data } = await axios.post(`${window.store.event_backend_api}/communications/mine/${update.id}/read`);
    unread.value = data.data.unread;
  } catch (e) {
    update.read = false;
  }
};

const react = async ({ message, emoji }) => {
  sendError.value = '';
  try {
    message.reactions = await reactToMine(message.id, emoji);
  } catch (e) {
    sendError.value = 'That reaction could not be saved. Please try again.';
  }
};

const ask = (question) => composer.value?.prefill(question);

const askAbout = (update) => {
  composer.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  composer.value?.prefill(`About “${update.subject}”: `);
};

// A draft another portal page handed over — “Ask about this policy”
// (XN-4556). Written into the box once, for them to edit, and never sent on
// its own; then taken out of the history entry so a reload or a Back does not
// write it in again. It waits for a box to write it in: after a failed read it
// stays in the entry for a later load (every poll tries, and after the first
// take the draft is null).
const takeDraft = async () => {
  const draft = window.history.state?.draft;
  if (typeof draft !== 'string' || draft === '') return;
  await nextTick();
  if (!composer.value) return;
  window.history.replaceState({ ...window.history.state, draft: null }, '');
  composer.value.prefill(draft);
};

watch(() => displayMessages.value.length, async () => {
  await nextTick();
  if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight;
});

let poll = null;
let stopListening = null;

onMounted(() => {
  eventStore.getEvent().catch(() => {});
  load();
  loadUpdates();
  loadOfficeHours();
  poll = setInterval(() => {
    now.value = new Date();
    load();
  }, POLL_MS);
  stopListening = listenForMessages(load);
});

onBeforeUnmount(() => {
  clearInterval(poll);
  stopListening?.();
});
</script>

<template>
  <div class="ph-msgs" data-xl-gen="northstar">
    <div class="ph-msgs__page">
      <h1 class="ph-msgs__title">Messages</h1>
      <p v-if="loading" class="ph-msgs__sub"><XlSkeleton width="min(440px, 85%)" height="0.8em" radius="7px" class="ph-msgs__sk" /></p>
      <p v-else class="ph-msgs__sub">{{ broker ? 'One person to talk to' : 'Somewhere to ask' }}, and everything {{ org }} needs to tell you.</p>

      <div class="ph-msgs__sh ph-msgs__sh--first">
        <h2 v-if="loading"><XlSkeleton width="120px" height="0.8em" radius="7px" class="ph-msgs__sk" /></h2>
        <h2 v-else>{{ broker ? 'Your broker' : 'Your conversation' }}</h2><i />
      </div>

      <!-- The conversation card as it will be — who, then the thread — with
           placeholders where the words go (CP Messages showed "Loading…"). -->
      <XlCard v-if="loading" class="ph-msgs__card" aria-busy="true" data-testid="messages-loading">
        <div class="ph-msgs__who">
          <XlSkeleton variant="circular" width="46px" class="ph-msgs__sk" />
          <div class="ph-msgs__who-text ph-msgs__who-text--loading">
            <XlSkeleton width="150px" height="0.9em" radius="7px" class="ph-msgs__sk" />
            <XlSkeleton width="230px" height="0.75em" radius="6px" class="ph-msgs__sk" />
          </div>
        </div>
        <div class="ph-msgs__scroll ph-msgs__scroll--loading">
          <XlSkeleton width="62%" height="42px" radius="16px" />
          <XlSkeleton width="48%" height="42px" radius="16px" class="ph-msgs__sk-mine" />
          <XlSkeleton width="56%" height="42px" radius="16px" />
        </div>
      </XlCard>
      <p v-else-if="loadError" role="alert" class="ph-msgs__state">{{ loadError }}</p>

      <template v-else>
        <XlCard class="ph-msgs__card" data-testid="broker-card">
          <div class="ph-msgs__who">
            <span class="ph-msgs__av" :class="{ 'ph-msgs__av--agency': !broker }" aria-hidden="true">
              <img v-if="broker?.photo" :src="broker.photo" alt="" data-testid="broker-photo" />
              <IconUserMultiple v-else-if="!broker" color="currentColor" size="22" />
              <template v-else>{{ initials }}</template>
            </span>
            <div class="ph-msgs__who-text">
              <span class="ph-msgs__name">
                <b data-testid="broker-name">{{ brokerName }}</b>
                <XlTag v-if="!broker" data-testid="being-assigned">Being assigned</XlTag>
              </span>
              <span class="ph-msgs__line" data-testid="broker-line">{{ whoLine }}</span>
            </div>
            <template v-if="broker && deskOpen">
              <XlButton
                variant="ghost"
                size="sm"
                :title="`Call ${brokerName} in Office Hours`"
                :aria-label="`Call ${brokerName} in Office Hours`"
                data-testid="office-hours-phone"
                @click="joinOfficeHours"
              >
                <template #icon>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6.4 4.2h2.7l1.4 3.9-1.8 1.4a11.2 11.2 0 0 0 5.8 5.8l1.4-1.8 3.9 1.4v2.7a1.8 1.8 0 0 1-2 1.8A15.6 15.6 0 0 1 4.6 6.2a1.8 1.8 0 0 1 1.8-2z" /></svg>
                </template>
              </XlButton>
              <XlButton
                variant="ghost"
                size="sm"
                :title="`Join ${brokerName} in Office Hours`"
                :aria-label="`Join ${brokerName} in Office Hours`"
                data-testid="office-hours-call"
                @click="joinOfficeHours"
              >
                <template #icon>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3.5" y="6.5" width="12" height="11" rx="2.5" /><path d="M15.5 11.2 20.5 8.6v6.8l-5-2.6z" /></svg>
                </template>
              </XlButton>
            </template>
          </div>

          <div ref="scroller" class="ph-msgs__scroll">
            <MessageList :messages="displayMessages" :other-name="brokerName" :agency-name="org" @join="(c) => (call = { room: c.room, host: false, leaveUrl: myCallLeaveUrl(c.id) })" @react="react">
              <template #before>
                <div class="ph-msgs__opener" data-testid="thread-opener">{{ opener }}</div>
                <div v-if="!displayMessages.length" class="ph-msgs__empty" data-testid="thread-empty">
                  <span class="ph-msgs__empty-ico" aria-hidden="true"><IconMail color="currentColor" size="22" /></span>
                  <b>{{ broker ? 'No messages yet' : 'Nothing sent yet' }}</b>
                  <p>{{ emptyLine }}</p>
                </div>
              </template>
            </MessageList>
          </div>

          <MessageComposer
            ref="composer"
            variant="portal"
            optimistic
            :placeholder="`Write to ${brokerName}…`"
            :rules="thread.attachment_rules"
            :submit="send"
          />
          <p v-if="sendError" role="alert" class="ph-msgs__error" data-testid="send-error">{{ sendError }}</p>
        </XlCard>

        <div class="ph-msgs__chips">
          <button v-for="q in QUICK" :key="q" type="button" class="xl-tag ph-msgs__chip" data-testid="quick-question" @click="ask(q)">{{ q }}</button>
        </div>
      </template>

      <template v-if="updates.length">
        <div class="ph-msgs__sh ph-msgs__sh--updates">
          <h2>From {{ org }}<template v-if="unread"> · {{ unread }} new</template></h2><i />
          <a v-if="unread" role="button" tabindex="0" @click="markAllRead" @keydown.enter="markAllRead">Mark all read</a>
        </div>
        <XlCard class="ph-msgs__card" data-testid="updates">
          <template v-for="u in updates" :key="u.id">
            <button
              type="button"
              class="ph-msgs__upd"
              :class="{ 'is-read': u.read, 'is-open': openUpdate === u.id }"
              :aria-expanded="openUpdate === u.id"
              data-testid="update-row"
              @click="toggleUpdate(u)"
            >
              <span class="ph-msgs__dot" aria-hidden="true" />
              <XlTag :tone="tagOf(u).tone" class="ph-msgs__tag" data-testid="update-tag">{{ tagOf(u).label }}</XlTag>
              <span class="ph-msgs__upd-title">{{ u.subject }}</span>
              <span class="ph-msgs__upd-when">{{ updateTime(u.sent_at) }}</span>
              <svg class="ph-msgs__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
            </button>
            <div v-if="openUpdate === u.id" class="ph-msgs__upd-body" data-testid="update-body">
              <SafeRichText class="ph-msgs__upd-text" :html="trimTail(u.body_html)" />
              <div class="ph-msgs__upd-actions">
                <XlButton v-if="u.tag === 'renewal' && hasPolicy" size="sm" data-testid="see-coverage" @click="router.push('/hub/coverage')">See my coverage</XlButton>
                <XlButton variant="ghost" size="sm" data-testid="ask-about" @click="askAbout(u)">Ask about this</XlButton>
              </div>
            </div>
          </template>
          <button v-if="hasEarlier" type="button" class="ph-msgs__earlier" data-testid="show-earlier" @click="showEarlier">Show earlier</button>
        </XlCard>
        <p class="ph-msgs__note">
          These come from {{ org }} automatically — you can't reply to them. To ask something, write to {{ brokerName }} above.
        </p>
      </template>
    </div>
  </div>

  <CallRoom v-if="call" :room="call.room" :host="call.host" :leave-url="call.leaveUrl" @close="call = null; load()" />
</template>

<style scoped lang="scss">
.ph-msgs {
  font-family: var(--xl-font-family);
  color: var(--xl-color-primary-text);
}

.ph-msgs__page {
  box-sizing: border-box;
}


.ph-msgs__title { margin: 0 0 8px; font-size: 32px; font-weight: 600; letter-spacing: -0.03em; }

.ph-msgs__sub { margin: 0 0 24px; font-size: 16.5px; color: var(--xl-color-secondary-text); }

.ph-msgs__sh {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin: 34px 0 14px;

  &--first { margin-top: 0; }

  h2 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--xl-color-outline-variant);
  }

  i { flex: 1 1 20px; height: 1px; background: var(--xl-color-border); }

  a { margin-left: auto; font-size: 13.5px; font-weight: 600; color: var(--xl-color-primary); cursor: pointer; white-space: nowrap; }
}

.ph-msgs__sh--updates {
  flex-wrap: wrap;
  row-gap: 8px;

  h2 { white-space: nowrap; }
}

.ph-msgs__card { padding: 0; overflow: hidden; margin-bottom: 14px; }

.ph-msgs__who {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--xl-color-border);
}

.ph-msgs__av {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: linear-gradient(150deg, var(--xl-color-surface-tint), var(--xl-color-primary));
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.ph-msgs__av--agency {
  background: var(--xl-color-border-subtle);
  color: var(--xl-color-outline-variant);
}

.ph-msgs__who-text {
  flex: 1;
  min-width: 0;

  b { font-size: 16px; font-weight: 700; }
}

.ph-msgs__name {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
}

.ph-msgs__line { display: block; font-size: 13px; color: var(--xl-color-outline); }

.ph-msgs__scroll {
  max-height: 330px;
  overflow-y: auto;
  padding: 18px 20px;
  background: var(--xl-color-surface);

  :deep(.msgs) { gap: 10px; }
}

.ph-msgs__opener {
  text-align: center;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--xl-color-secondary-text);
  background: var(--xl-color-border-subtle);
  border-radius: 10px;
  padding: 10px 13px;
}

.ph-msgs__empty {
  text-align: center;
  padding: 26px 20px 22px;

  b { display: block; margin-bottom: 5px; font-size: 15px; }

  p {
    max-width: 34ch;
    margin: 0 auto;
    font-size: 13.5px;
    line-height: 1.55;
    color: var(--xl-color-outline);
  }
}

.ph-msgs__empty-ico {
  width: 44px;
  height: 44px;
  margin: 0 auto 12px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--xl-color-primary-container);
  color: var(--xl-color-primary);
}

.ph-msgs__chips { display: flex; gap: 7px; margin: 12px 0 0; flex-wrap: wrap; }

.ph-msgs__chip { height: 30px; padding: 0 13px; border: 0; line-height: 1; cursor: pointer; }

.ph-msgs__error { margin: 0 16px 12px; font-size: 13px; color: var(--xl-color-error); }

.ph-msgs__upd {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border: 0;
  border-bottom: 1px solid var(--xl-color-border-subtle);
  background: none;
  font: inherit;
  letter-spacing: inherit;
  color: var(--xl-color-primary-text);
  text-align: left;
  cursor: pointer;

  &:focus:not(:focus-visible) { outline: none; box-shadow: none; }
  &:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: -2px; }
}

.ph-msgs__chev { flex: 0 0 auto; color: var(--xl-color-outline-variant); transition: transform 0.16s; }

.ph-msgs__upd.is-open .ph-msgs__chev { transform: rotate(180deg); }

.ph-msgs__upd-text {
  :deep(> p:first-child) { margin-top: 0; }
  :deep(> p:last-child) { margin-bottom: 0; }
}

.ph-msgs__upd-actions { margin-top: 12px; display: flex; gap: 8px; flex-wrap: wrap; }

.ph-msgs__dot { width: 7px; height: 7px; flex: 0 0 7px; border-radius: 50%; background: var(--xl-color-primary); }

.ph-msgs__upd.is-read .ph-msgs__dot { background: transparent; }

.ph-msgs__upd-title {
  flex: 1;
  min-width: 0;
  font-size: 14.5px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ph-msgs__upd.is-read .ph-msgs__upd-title { font-weight: 400; color: var(--xl-color-secondary-text); }

.ph-msgs__tag { flex: 0 0 auto; }

.ph-msgs__upd-when { flex: 0 0 auto; font-size: 12.5px; color: var(--xl-color-outline-variant); white-space: nowrap; }

.ph-msgs__earlier {
  width: 100%;
  padding: 12px 20px;
  border: 0;
  background: none;
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--xl-color-primary);
  cursor: pointer;
}

.ph-msgs__upd-body {
  padding: 0 20px 18px 46px;
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--xl-color-secondary-text);
  background: var(--xl-color-primary-container);
  border-bottom: 1px solid var(--xl-color-border-subtle);
}

@media (max-width: 480px) {
  .ph-msgs__upd {
    display: grid;
    grid-template-columns: 7px auto 1fr auto;
    grid-template-areas: 'dot tag when chev' '. title title title';
    gap: 6px 10px;
    padding: 12px 16px;
  }

  .ph-msgs__dot { grid-area: dot; }
  .ph-msgs__tag { grid-area: tag; }
  .ph-msgs__upd-when { grid-area: when; justify-self: end; }
  .ph-msgs__chev { grid-area: chev; }
  .ph-msgs__upd-title { grid-area: title; white-space: normal; }
  .ph-msgs__upd-body { padding: 0 16px 16px 33px; }
}

.ph-msgs__note { font-size: 13px; color: var(--xl-color-outline); margin-top: 14px; }

.ph-msgs__state { color: var(--xl-color-outline); font-size: 14px; }

.ph-msgs__sk { width: auto; vertical-align: middle; flex: 0 0 auto; }
.ph-msgs__who-text--loading { display: flex; flex-direction: column; gap: 8px; }
.ph-msgs__scroll--loading { display: flex; flex-direction: column; gap: 10px; min-height: 180px; }
.ph-msgs__sk-mine { align-self: flex-end; }

</style>
